import {
    occursUntilPlaceHolder,
    occursUntilAriaPlaceHolder,
    recurrenceUntilAriaPlaceholder,
    occursPlaceHolder,
    occursAriaPlaceHolder,
} from './getRecurrenceStrings.locstring.json';
import loc, { format } from 'owa-localize';
import { CalendarEvent } from 'owa-calendar-types';
import { END_DATE_RECURRENCE } from './constants';

import { formatShortMonthDayYear, owaDate, startOfDay, mergeDateAndTime } from 'owa-datetime';

import EndDateRecurrence from 'owa-service/lib/contract/EndDateRecurrence';
import getRecurrenceHandler from 'owa-timeformat/lib/utils/recurrence/getRecurrenceHandler';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';

/**
 * Gets the recurrence strings that should be displayed in the recurrence editor,
 * either in quick compose of full compose.
 *
 * @param timeZoneId
 * The time zone in which dates are shown.
 * In quick compose it will be the user's time zone regardless of which time zone the event was created in.
 * In full compose, it will be the event's time zone.
 *
 * @param event
 * The event being edited, from where we read its Start time and its original StartTimeZoneId.
 *
 * @param recurrence
 * The recurrence object being edited.
 * This might be the event's recurrence itself, like when the editor is embedded in quick compose,
 * or a separate instance, like when the editor is in a popup in full compose.
 *
 * ### NOTES:
 * The server sends the recurrenceRange.EndDate in the user's time zone.
 * It contains the start of the day of the date in the event's time zone.
 * If we simply convert this date to the desired time zone as-is we might get the wrong
 * display value because the start time of the event might push the event to another day
 * (in the target time zone).
 *
 * For example: create two recurring events from 3/18-3/20 in PST, one at 2am and another at 3am:
 * 3/18 2am in PST => 3/18 23 in UTC+14
 * 3/18 3am in PST => 3/19 00 in UTC+14
 *
 * With OWA using PST we get this from the server:
 * Subject: "2AM in PST, 23 in UTC+14, 3/18-3/20 PST"
 * Start: "2019-03-18T02:00:00-07:00"
 * StartTimeZoneId: "Pacific Standard Time"
 * RecurrenceRange.StartDate: "2019-03-18T00:00:00-07:00"
 * RecurrenceRange.EndDate: "2019-03-20T00:00:00-07:00"
 *
 * Subject: "3AM in PST, 0(+1) in UTC+14, 3/18-3/20 PST"
 * Start: "2019-03-18T03:00:00-07:00"
 * StartTimeZoneId: "Pacific Standard Time"
 * RecurrenceRange.StartDate: "2019-03-18T00:00:00-07:00"
 * RecurrenceRange.EndDate: "2019-03-20T00:00:00-07:00"
 *
 * With OWA using UTC+14 we get this from the server:
 * Subject: "2AM in PST, 23 in UTC+14, 3/18-3/20 PST"
 * Start: "2019-03-18T23:00:00+14:00"
 * StartTimeZoneId: "Pacific Standard Time"
 * RecurrenceRange.StartDate: "2019-03-18T21:00:00+14:00"
 * RecurrenceRange.EndDate: "2019-03-20T21:00:00+14:00"
 * (this must show from 3/18 to 3/20, so in principle it looks ok)
 *
 * Subject: "3AM in PST, 0(+1) in UTC+14, 3/18-3/20 PST"
 * Start: "2019-03-19T00:00:00+14:00"
 * StartTimeZoneId: "Pacific Standard Time"
 * RecurrenceRange.StartDate: "2019-03-18T21:00:00+14:00"
 * RecurrenceRange.EndDate: "2019-03-20T21:00:00+14:00"
 * (in this one we face a problem if we don't check the Start & StartTimeZoneId values)
 * The event will show from 3/19 to 3/21 in the surface (since it accounts for start time).
 *
 * So in quick-compose recurrence must show from 3/19 to 3/21 as well since it shows values in UTC+14.
 * But the StartDate and EndDate are the same as the event above, so we can't rely on them alone.
 */
export default function getRecurrenceStrings(
    timeZoneId: string,
    { Start, StartTimeZoneId }: Pick<CalendarEvent, 'Start' | 'StartTimeZoneId'>,
    recurrence: RecurrenceType
) {
    if (!recurrence) {
        return {
            recurrenceEnd: undefined,
            recurrenceDisplayDate: null,
            recurrenceString: null,
            ariaLabel: null,
            recurrenceSummaryAriaLabel: null,
        };
    }

    const recurrenceHandler = getRecurrenceHandler(recurrence.RecurrencePattern);
    const summary = recurrenceHandler.formatRecurrence(recurrence.RecurrencePattern);

    if (recurrence.RecurrenceRange.__type == END_DATE_RECURRENCE) {
        const recurrenceRange = recurrence.RecurrenceRange as EndDateRecurrence;

        const recurrenceEndDateInEventTz = owaDate(StartTimeZoneId, recurrenceRange.EndDate);
        const eventStartInEventTz = owaDate(StartTimeZoneId, Start);
        const lastEventInEventTz = mergeDateAndTime(
            recurrenceEndDateInEventTz,
            eventStartInEventTz
        );

        const lastEventInTargetTz = owaDate(timeZoneId, lastEventInEventTz);
        const recurrenceEnd = startOfDay(lastEventInTargetTz);
        const recurrenceDisplayDate = formatShortMonthDayYear(recurrenceEnd);
        return {
            recurrenceEnd,
            recurrenceDisplayDate,
            recurrenceString: format(
                loc(occursUntilPlaceHolder),
                recurrenceHandler.formatOptionsForSummary(recurrence.RecurrencePattern)
            ),
            ariaLabel: format(loc(occursUntilAriaPlaceHolder), summary, recurrenceDisplayDate),
            recurrenceSummaryAriaLabel: format(
                loc(recurrenceUntilAriaPlaceholder),
                summary,
                recurrenceDisplayDate
            ),
        };
    }

    return {
        recurrenceEnd: undefined,
        recurrenceDisplayDate: null,
        recurrenceString: format(loc(occursPlaceHolder), summary),
        ariaLabel: format(loc(occursAriaPlaceHolder), summary),
        recurrenceSummaryAriaLabel: summary,
    };
}
